import { useSyncExternalStore } from 'react';
import { typedFromEntries } from '../functional';

type BreakPoint = 'sm' | 'md' | 'lg' | 'xl';

type Breakpoints = Record<BreakPoint, number>;

const breakPoints: Breakpoints = {
  lg: 1024,
  md: 768,
  sm: 640,
  xl: 1280,
} satisfies Breakpoints;

const breakPointKeys = Object.keys(breakPoints) as BreakPoint[];

const createMediaQuery = (query: string) => {
  const mql =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query)
      : null;

  return {
    subscribe: (cb: () => void) => {
      mql?.addEventListener('change', cb);
      return () => mql?.removeEventListener('change', cb);
    },
    getSnapshot: () => mql?.matches ?? false,
    getServerSnapshot: () => false,
  };
};

const minQueries = typedFromEntries(
  breakPointKeys.map((key) => [
    key,
    createMediaQuery(`(min-width: ${breakPoints[key]}px)`),
  ]),
);

const useIsAboveBreakpoint = (bp: BreakPoint) => {
  const q = minQueries[bp];
  return useSyncExternalStore(q.subscribe, q.getSnapshot, q.getServerSnapshot);
};

export const useScreenSize = () => {
  const isAbove: Record<BreakPoint, boolean> = {
    sm: useIsAboveBreakpoint('sm'),
    md: useIsAboveBreakpoint('md'),
    lg: useIsAboveBreakpoint('lg'),
    xl: useIsAboveBreakpoint('xl'),
  };

  return {
    isBetween: (min: BreakPoint, max: BreakPoint) =>
      isAbove[min] && !isAbove[max],
    isLargerThan: (breakPoint: BreakPoint) => isAbove[breakPoint],
    isSmallerThan: (breakPoint: BreakPoint) => !isAbove[breakPoint],
  };
};
